import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  InputAdornment,
  IconButton,
  Divider,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Person,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Error as ErrorIcon,
  Email,
  Business,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {api} from '../config/API_URL';

export default function Join() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('code');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});

  // Load invite details when component mounts
  useEffect(() => {
    if (!inviteCode) {
      setError('Invalid invite link. No code provided.');
      setLoading(false);
      return;
    }

    loadInviteDetails();
  }, [inviteCode]);

  const loadInviteDetails = async () => {
    try {
      const response = await api.get(`/auth/invite/${inviteCode}`);
      
      if (response.data.success) {
        setInviteData(response.data.invite);
      } else {
        setError(response.data.message || 'Invalid invite code');
      }
    } catch (err) {
      console.error('Failed to load invite:', err);
      setError(
        err.response?.data?.message || 
        'Failed to load invite. The link may be expired or invalid.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleJoin = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await api.post('/auth/compliance/join', {
        invite_code: inviteCode,
        username: formData.username,
        password: formData.password,
      });

      if (response.data.success) {
        // Store auth token
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('org', JSON.stringify(response.data.org));

        // Redirect to dashboard
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Join error:', err);
      setError(
        err.response?.data?.message || 
        'Failed to join organization. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'white' }}>
            Loading invite details...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Invalid invite state
  if (!inviteData && error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          px: 2,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card sx={{ maxWidth: 500 }}>
            <CardContent sx={{ p: 5, textAlign: 'center' }}>
              <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                Invalid Invite
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {error}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </Box>
    );
  }

  const getRoleBadge = (role) => {
    const roleConfig = {
      owner: { color: 'warning', label: 'Owner' },
      manager: { color: 'primary', label: 'Manager' },
      member: { color: 'default', label: 'Staff Member' },
    };

    const config = roleConfig[role] || roleConfig.member;

    return (
      <Chip
        label={config.label}
        color={config.color}
        sx={{ fontWeight: 600 }}
      />
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        px: 2,
        py: 4,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%', maxWidth: 550 }}
      >
        <Card
          sx={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}
        >
          <CardContent sx={{ p: 5 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
              >
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontSize: 32,
                    fontWeight: 700,
                  }}
                >
                  {inviteData?.org_name?.split(' ').map(w => w[0]).join('') || 'ORG'}
                </Avatar>
              </motion.div>

              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Join {inviteData?.org_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You've been invited to join the team
              </Typography>
            </Box>

            {/* Invite Info */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                borderLeft: 4,
                borderColor: 'success.main',
                p: 3,
                borderRadius: 2,
                mb: 4,
              }}
            >
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person sx={{ color: 'success.dark' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Invited by
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {inviteData?.invited_by_name || 'Organization Admin'}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business sx={{ color: 'success.dark' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Organization
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {inviteData?.org_name}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ color: 'success.dark' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Your Email
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {inviteData?.email}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle sx={{ color: 'success.dark' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Your Role
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {getRoleBadge(inviteData?.role)}
                    </Box>
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Signup Form */}
            <form onSubmit={handleJoin}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Create Your Account
                  </Typography>
                </Box>

                <TextField
                  label="Username"
                  value={formData.username}
                  onChange={handleChange('username')}
                  error={!!errors.username}
                  helperText={errors.username || 'Choose a unique username'}
                  fullWidth
                  required
                  placeholder="johndoe"
                  disabled={submitting}
                />

                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange('password')}
                  error={!!errors.password}
                  helperText={errors.password || 'Must be at least 8 characters'}
                  fullWidth
                  required
                  disabled={submitting}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  fullWidth
                  required
                  disabled={submitting}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={submitting}
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={24} />
                  ) : (
                    'Accept Invite & Create Account'
                  )}
                </Button>
              </Stack>
            </form>

            {/* Footer */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                By joining, you agree to the terms and conditions
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
}
